import { Box, GlobalStyles, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useMeasure } from 'react-use';

import { InteractionParadigmContext } from '@/contexts/InteractionParadigm.context';
import { BalanceSheetTable } from '@/modules/balanceSheet/BalanceSheetTable/BalanceSheetTable';
import {
  buildFlatRows,
  FlatRow,
  mapDataToRows,
} from '@/modules/balanceSheet/BalanceSheetTable/BalanceSheetTable.utils';
import { BalanceSheetTable_BalanceSheetFragment } from '@/modules/balanceSheet/BalanceSheetTable/graphql/BalanceSheetTable.fragments.generated';
import { DataTableSlide } from '@/modules/presentation/components/DataTableSlide';
import { PRESENTATION_SPACING_UNITS } from '@/modules/presentation/presentation.constants';
import { PresentationSlide } from '@/modules/presentation/PresentationSlide';
import { PDF_SERVICE_VIEWPORT_WIDTH_PX } from '@/modules/presentation/PresentationSlide/PresentationSlide.constants';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';

import { PresentationDataTableSlide } from '../../PresentationDataTableSlide';
import { paginateBalanceSheetRows } from '../BalanceSheet/BalanceSheetSlide.paginationUtils';
import { useEntityBalanceSheetSlideQuery } from './graphql/EntityBalanceSheetSlide.generated';

const PRINT_PX_PER_INCH = PDF_SERVICE_VIEWPORT_WIDTH_PX / 11;

export interface EntityBalanceSheetPageProps {
  entityName: string;
  valuationDate?: Date | null;
  pageIndex: number;
  pageCount: number;
  pageRows: FlatRow[];
  balanceSheet: BalanceSheetTable_BalanceSheetFragment | null;
  loading: boolean;
  error: boolean;
  slideId: string;
  SlideWrapper?: React.ElementType;
  standalone?: boolean;
  isExportPage?: boolean;
  isPresenting?: boolean;
}

export function EntityBalanceSheetPage({
  entityName,
  valuationDate,
  pageIndex,
  pageCount,
  pageRows,
  balanceSheet,
  loading,
  error,
  slideId,
  SlideWrapper = Box,
  standalone = false,
  isExportPage = false,
  isPresenting = false,
}: EntityBalanceSheetPageProps) {
  const [exportRef, { width, height }] = useMeasure<HTMLDivElement>();
  const TableSlide = standalone ? DataTableSlide : PresentationDataTableSlide;
  const intrinsic = isExportPage && !isPresenting;
  const Wrapper = intrinsic ? Box : SlideWrapper;
  const pageName = `entity-bs-${slideId}`;
  let sizing: 'intrinsic' | 'fit-content' | undefined;
  if (intrinsic) sizing = 'intrinsic';
  else if (isExportPage) sizing = 'fit-content';
  return (
    <>
      {isExportPage && (
        <GlobalStyles
          styles={`
      @page ${pageName} { size: ${Math.max(11, width / PRINT_PX_PER_INCH)}in ${Math.max(8.5, height / PRINT_PX_PER_INCH)}in; margin: 0; }
      .${pageName} { page: ${pageName}; }
    `}
        />
      )}
      <Wrapper
        ref={intrinsic ? exportRef : undefined}
        className={isExportPage ? pageName : undefined}
        sx={
          intrinsic
            ? {
                bgcolor: 'white',
                alignSelf: 'flex-start',
                width: 'max-content',
                overflow: 'hidden',
              }
            : undefined
        }
      >
        <TableSlide
          id={slideId}
          sizing={sizing}
          leftHeaderContent={
            <PresentationSlide.MainHeading
              heading={entityName}
              subheading="Balance sheet"
            />
          }
          rightHeaderContent={
            standalone && valuationDate ? (
              <PresentationSlide.SecondaryHeading
                lineOne={`As of ${formatDateToMMDDYY(valuationDate)}`}
              />
            ) : undefined
          }
        >
          <Stack
            spacing={2}
            justifyContent="flex-start"
            px={PRESENTATION_SPACING_UNITS}
            height={isExportPage ? undefined : '100%'}
            data-presentationdatatableready={!loading && !error}
          >
            {error ? (
              <Typography variant="body-m">
                Balance sheet data could not be loaded.
              </Typography>
            ) : (
              <InteractionParadigmContext.Provider value={{ viewOnly: true }}>
                <BalanceSheetTable
                  showEmptyState={false}
                  balanceSheet={balanceSheet}
                  loading={loading}
                  defaultExpandAll
                  density={isExportPage ? 'extra-dense' : 'default'}
                  flatRowsOverride={pageRows}
                />
              </InteractionParadigmContext.Provider>
            )}
            {!loading && !error && pageRows.length === 0 && (
              <Typography variant="body-m">No recorded values.</Typography>
            )}
            {pageIndex < pageCount - 1 && (
              <Stack direction="row" justifyContent="flex-end" width="100%">
                <Typography variant="body-xs">
                  Continued on next page
                </Typography>
              </Stack>
            )}
          </Stack>
        </TableSlide>
      </Wrapper>
    </>
  );
}

export interface EntityBalanceSheetSlideProps {
  entityId: string;
  entityName: string;
  slideIdPrefix: string;
  SlideWrapper?: React.ElementType;
  standalone?: boolean;
  isExportPage?: boolean;
  isPresenting?: boolean;
  renderPage?: (props: EntityBalanceSheetPageProps) => React.ReactNode;
}

export function useEntityBalanceSheetPages(
  entityId?: string,
  isExportPage = false
) {
  // Like the household balance sheet slide, this slide owns its report query.
  // The same query serves standalone exports, and the presentation wrapper
  // mounts it only when visible rather than loading every entity report upfront.
  const { data, loading, error } = useEntityBalanceSheetSlideQuery({
    variables: { entityId: entityId ?? '' },
    skip: !entityId,
  });
  const balanceSheet = data?.entityBalanceSheet ?? null;
  const valuationDate =
    data?.entity?.__typename === 'Entity'
      ? data.entity.subtype.mostRecentValuationDate
      : null;
  const pages = useMemo(() => {
    if (!balanceSheet) return [{ rows: [], isLastPage: true }];
    const rows = mapDataToRows(balanceSheet, {
      isViewOnly: true,
      includeChildren: true,
    });
    const flatRows = buildFlatRows(rows, new Set(rows.map((row) => row.id)));
    if (isExportPage) return [{ rows: flatRows, isLastPage: true }];
    return paginateBalanceSheetRows(flatRows, {
      showColumnGroups: false,
      detailsInColumn: true,
    });
  }, [balanceSheet, isExportPage]);

  return { balanceSheet, valuationDate, pages, loading, error };
}

export function EntityBalanceSheetSlide({
  entityId,
  entityName,
  slideIdPrefix,
  SlideWrapper,
  standalone,
  isExportPage,
  isPresenting,
  renderPage,
}: EntityBalanceSheetSlideProps) {
  const { balanceSheet, valuationDate, pages, loading, error } =
    useEntityBalanceSheetPages(entityId, isExportPage);

  return (
    <>
      {pages.map((page, pageIndex) => {
        const props: EntityBalanceSheetPageProps = {
          entityName,
          valuationDate,
          pageIndex,
          pageCount: pages.length,
          pageRows: page.rows,
          balanceSheet,
          loading,
          error: Boolean(error),
          slideId: `${slideIdPrefix}-${pageIndex}`,
          SlideWrapper,
          standalone,
          isExportPage,
          isPresenting,
        };
        return renderPage ? (
          renderPage(props)
        ) : (
          <EntityBalanceSheetPage key={props.slideId} {...props} />
        );
      })}
    </>
  );
}
